export default customElements.define('w-field-checkbox', class FieldCheckbox extends wealthify.componentBases.FieldSelection {
    constructor () {

        const config = {
            canHaveMultipleInputs: true,
            cacheDom: ['data-fieldset'],
        };

        super(config);

        this.addEventListener('change', this.#applyValueFromInputs);
        this.addEventListener(this.events.ValueChange.name, this.#applyValueToInputs);

        this.onConnectedCallback = () => {
            if (this.validateMaxlength !== undefined) {
                this.#addRestrictSelectionCheck();
            }

            this.#applyValueToInputs();
        };
    }

    // Static

    static styleSelector = 'c-field-checkbox';

    static validationProps = [
        wealthify.FieldValidation.validationPropEnums.MaxLength,
        wealthify.FieldValidation.validationPropEnums.MinLength,
    ]

    static template = `
        <div>
            <input class='${this.styleSelector}__item-input' type='checkbox' data-fieldinput>
            <label class='${this.styleSelector}__item-label' data-fieldinputlabel></label>
        </div>
    `;

    static get validLayouts () {
        return [
            'stacked', 'inline', 'inline-span-33', 'inline-span-50'
        ];
    }

    static get observedProps () {
        return [
            new wealthify.Prop('layout-tablet', {
                default: 'stacked',
                validators: [wealthify.propValidators.IsInArray(this.validLayouts)]
            }),
            new wealthify.Prop('layout-desktop', {
                default: 'stacked',
                validators: [wealthify.propValidators.IsInArray(this.validLayouts)]
            }),
        ];
    }

    // Public

    get propCallbacks () {
        return {
            layoutTablet: () => this.#applyLayout(this.layoutTablet, 'layout-tablet'),
            layoutDesktop: () => this.#applyLayout(this.layoutDesktop, 'layout-desktop'),
        };
    }

    addFieldInputElement () {
        super.addFieldInputElement();
        this.#updateFieldsetLegend();
    }

    formatFieldInputContent () {
        this.#fieldInputs.forEach((input, index) => {
            const id = `${this.name}-input-${index + 1}`;
            const name = `${this.name}-input`;
            
            const inlineLabel = input.closest('[data-item-container]').querySelector('[data-fieldinputlabel]');

            input.id = id;
            input.name = name;

            if (inlineLabel) {
                inlineLabel.htmlFor = id;
            }

        });
    }

    // Private

    get #fieldInputs () {
        if (!this.options.length) {
            return [];
        }
        
        // if only one option is present and it isn't already an array
        // then convert it to an array
        return this.options.length > 1 || Array.isArray(this.dom.fieldinput) ? this.dom.fieldinput : [this.dom.fieldinput];
    }

    #applyValueFromInputs () {
        this.#convertValueToArray();
        this.#updateValue();
    }

    #applyValueToInputs () {
        this.#convertValueToArray();

        const isValidValueOrEmpty = this.#isValidValueOrEmpty();

        if (isValidValueOrEmpty) {
            this.#updateInputs();
        } else {
            this.#updateValue();
        }
    }

    #convertValueToArray () {
        if (['', undefined].includes(this.value)) {
            this.value = [];

            return;
        }

        if (!Array.isArray(this.value)) {
            this.value = wealthify.type.array.toArray(this.value);
        }
    }

    #updateValue () {
        const checkedInputs = this.#fieldInputs.filter((input) => input.checked);

        this.value = checkedInputs.map((input) => input.value);
    }

    #updateFieldsetLegend() {
        if (!this.options) {
            return;
        }

        // if only one checkbox option is present then the option label
        // is sufficient, and the (required) component label can be hidden
        const classListAction = this.options.length === 1 ? 'add' : 'remove';

        this.dom.fieldlegend.classList[classListAction]('u-display__none');
    }

    #isValidValueOrEmpty () {
        const validValues = this.options.map((option) => option.value);

        return this.value.some((value) => validValues.includes(value) ) || !this.value.length;
    }

    #updateInputs () {
        this.#fieldInputs.forEach((input, index) => {
            const isChecked = this.value !== undefined && this.value.includes(input.value);
            const selectedClass = `${this.styleSelector}__item-container--selected`;

            input.checked = isChecked;

            if(Array.isArray(this.dom.itemContainer)){
                this.dom.itemContainer[index].classList[isChecked ? 'add' : 'remove'](selectedClass);
            }else{
                this.dom.itemContainer.classList[isChecked ? 'add' : 'remove'](selectedClass);
            }
        });
    }

    #addRestrictSelectionCheck () {
        this.#fieldInputs.forEach((input) => {
            input.addEventListener('change', (event) => {
                this.#restrictSelectionCheck(event.target);
            });
        });
    }

    #restrictSelectionCheck (target) {
        // Change event happens before value & UI updates.
        // So increment / decrement value length based on target checked
        const valueLength = this.value.length + (target.checked ? 1 : -1);
        const maxLengthReached = valueLength >= this.validateMaxlength;
        const uncheckedInputs = this.dom.fieldinput.filter((input) => !input.checked);

        uncheckedInputs.forEach((input) => {
            input.disabled = maxLengthReached;
        });
    }
    
    #applyLayout(layoutValue, layoutClass) {
        this.constructor.validLayouts.forEach(type => {
            const classes = Array.from(this.styleSelectors).map(selector => `${selector}--${layoutClass}-${type}`);
            const classListAction = type === layoutValue ? 'add' : 'remove';

            this.classList[classListAction](...classes);
        });
    }
});